// =============================================================================
// FASE A — Step Cambio Veicolo / Vendita / Rottamazione
// Raccoglie: targa vecchio veicolo + documenti dedicati + docs convenzione
// Mostrato SOLO quando tipo_richiesta = "cambio_veicolo".
// Sub-operazioni:
//   - "cambio":                targa + doc vendita/rott. + libretto nuovo + ID + badge
//   - "vendita_rottamazione":  targa + doc vendita/rott. + IBAN         + ID + badge
// =============================================================================
import React from "react";
import { Info, AlertCircle } from "lucide-react";
import { DocUploader } from "./DocUploader";
import { DOC_IDS } from "../../hooks/useRcaWizard";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";

// Regex targa italiana standard (AB123CD) — permissiva
const TARGA_RE = /^[A-Z]{2}\d{3}[A-Z]{2}$/;

const DOC_META = {
  [DOC_IDS.DOC_VENDITA_ROTTAMAZIONE]: {
    label: "Documento di vendita o certificato di rottamazione",
    desc:
      "Serve a certificare che il vecchio veicolo non è più tuo o è stato " +
      "rottamato (atto di vendita, certificato del centro autorizzato, radiazione PRA).",
  },
  [DOC_IDS.LIB_NUOVO_VEICOLO]: {
    label: "Libretto del nuovo veicolo",
    desc:
      "Ci serve per intestare correttamente la nuova polizza al veicolo giusto " +
      "(carta di circolazione fronte/retro).",
  },
  [DOC_IDS.DOC_IBAN]: {
    label: "IBAN per il rimborso",
    desc:
      "Serve per il rimborso dell'eventuale premio non goduto. Carica un estratto " +
      "conto oppure una carta intestata della banca (con IBAN chiaramente leggibile).",
  },
  [DOC_IDS.DOC_ID_DIP]: {
    label: "Documento d'identità del dipendente",
    desc: "Carta d'identità o patente in corso di validità (fronte/retro).",
  },
  [DOC_IDS.BADGE_DIP]: {
    label: "Badge aziendale del dipendente",
    desc:
      "Foto o scansione del badge attivo che attesta il tuo diritto alla convenzione.",
  },
};

export function Step3bCambioVeicolo({
  state, setField, docs, requiredDocs, setDoc, removeDoc,
}) {
  const isCambio = state.sub_operazione === "cambio";
  const isVendita = state.sub_operazione === "vendita_rottamazione";
  const reqSet = new Set(requiredDocs.required);
  const optSet = new Set(requiredDocs.optional);

  // Ordine di rendering doc: prima operazione, poi convenzione
  const orderedDocs = [
    DOC_IDS.DOC_VENDITA_ROTTAMAZIONE,
    ...(isCambio ? [DOC_IDS.LIB_NUOVO_VEICOLO] : []),
    ...(isVendita ? [DOC_IDS.DOC_IBAN] : []),
    DOC_IDS.DOC_ID_DIP,
    DOC_IDS.BADGE_DIP,
  ].filter((d) => reqSet.has(d) || optSet.has(d));

  const targaUpper = (state.targa_veicolo_cambiato || "").toUpperCase();
  const targaValid = !targaUpper || TARGA_RE.test(targaUpper);

  return (
    <div className="space-y-4" data-testid="rca-step-cambio-veicolo">
      <div>
        <h2 className="text-lg sm:text-xl font-semibold text-[#0F1F14]">
          {isCambio
            ? "Cambio veicolo: dati e documenti"
            : "Vendita o rottamazione: dati e documenti"}
        </h2>
        <p className="text-sm text-[#4A5E51] mt-1">
          {isCambio
            ? ("Indicaci la targa del veicolo che stai sostituendo e carica il "
                + "documento di vendita/rottamazione insieme al libretto del nuovo "
                + "veicolo. Ti chiediamo solo il minimo indispensabile per gestire "
                + "la pratica con la compagnia.")
            : ("Indicaci la targa del veicolo venduto o rottamato, il documento "
                + "che lo attesta e un IBAN dove trasferire l'eventuale rimborso "
                + "del premio non goduto.")}
        </p>
      </div>

      {/* Targa veicolo cambiato/venduto */}
      <div className="rounded-2xl border border-[#E5E2DB] bg-white p-4">
        <Label className="text-xs" htmlFor="rca-targa-vecchio">
          Targa del veicolo {isCambio ? "cambiato" : "venduto o rottamato"} *
        </Label>
        <Input
          id="rca-targa-vecchio"
          value={targaUpper}
          onChange={(e) => setField("targa_veicolo_cambiato",
             e.target.value.toUpperCase().replace(/\s/g, ""))}
          maxLength={7}
          placeholder="AB123CD"
          data-testid="rca-cambio-targa-vecchio"
          className={!targaValid ? "border-red-400" : ""}
        />
        <p className="text-[11px] text-[#4A5E51] mt-1">
          Formato standard italiano: 2 lettere + 3 numeri + 2 lettere (es. AB123CD).
        </p>
        {!targaValid && (
          <p
            className="text-[11px] text-red-700 mt-1 flex items-center gap-1"
            data-testid="rca-cambio-targa-err"
          >
            <AlertCircle className="w-3 h-3" />
            Targa non valida.
          </p>
        )}
      </div>

      {/* Blocco documenti operazione */}
      <div>
        <h3 className="text-sm font-semibold text-[#0F1F14] mb-2">
          Documenti dell&apos;operazione
        </h3>
        <div className="space-y-3">
          {orderedDocs
            .filter((d) =>
              d === DOC_IDS.DOC_VENDITA_ROTTAMAZIONE
              || d === DOC_IDS.LIB_NUOVO_VEICOLO
              || d === DOC_IDS.DOC_IBAN)
            .map((docId) => (
              <DocUploader
                key={docId}
                docId={docId}
                label={DOC_META[docId]?.label || docId}
                description={DOC_META[docId]?.desc}
                required={reqSet.has(docId)}
                optional={optSet.has(docId)}
                currentDoc={docs[docId]}
                onUploaded={(id, p) => setDoc(id, { ...p, status: "caricato" })}
                onCleared={(id) => removeDoc(id, false)}
                testid={`rca-doc-${docId}`}
              />
            ))}
        </div>
      </div>

      {/* Blocco documenti convenzione */}
      <div>
        <div className="flex items-start gap-2 mb-2">
          <Info className="w-4 h-4 text-[#4A5E51] mt-0.5 flex-shrink-0" />
          <h3 className="text-sm font-semibold text-[#0F1F14]">
            Documenti per la convenzione
          </h3>
        </div>
        <div className="space-y-3">
          {orderedDocs
            .filter((d) => d === DOC_IDS.DOC_ID_DIP || d === DOC_IDS.BADGE_DIP)
            .map((docId) => (
              <DocUploader
                key={docId}
                docId={docId}
                label={DOC_META[docId]?.label || docId}
                description={DOC_META[docId]?.desc}
                required={reqSet.has(docId)}
                optional={optSet.has(docId)}
                currentDoc={docs[docId]}
                onUploaded={(id, p) => setDoc(id, { ...p, status: "caricato" })}
                onCleared={(id) => removeDoc(id, false)}
                testid={`rca-doc-${docId}`}
              />
            ))}
        </div>
      </div>
    </div>
  );
}
